import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { txToast } from "@/components/TxToaster";
import { useAdminPerms } from "@/hooks/auth";
import { useOpenConfirmDialog } from "@/hooks/dialogs";
import { useBackendApi } from "@/hooks/fetch";
import type {
    AdminActionResp,
    AdminDeleteReq,
    AdminGroup,
    AdminGroupDeleteReq,
    AdminListItem,
    AdminManagerListResp,
} from "@shared/adminApiTypes";
import type { GenericApiOkResp } from "@shared/genericApiTypes";
import { ShieldIcon, PencilIcon, Trash2Icon, PlusIcon, Loader2Icon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import AdminEditDialog from "./AdminEditDialog";
import AdminGroupsDialog from "./AdminGroupsDialog";
import GroupEditDialog from "./GroupEditDialog";
import UnauthorizedPage from "@/pages/UnauthorizedPage";

//`undefined` means "no dialog open", `null` means "creating a new one"
type GroupDialogState = undefined | null | AdminGroup;
type AdminDialogState = undefined | null | AdminListItem;


function GroupCard({ group, onEdit, onDelete }: {
    group: AdminGroup;
    onEdit: () => void;
    onDelete: () => void;
}) {
    const permCount = group.permissions.includes('all_permissions')
        ? 'Alle Berechtigungen'
        : `${group.permissions.length} ${group.permissions.length === 1 ? 'Berechtigung' : 'Berechtigungen'}`;
    const adminCount = `${group.admins.length} ${group.admins.length === 1 ? 'Admin' : 'Admins'}`;

    return (
        <div className="surface p-4 space-y-3">
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <h3 className="text-title truncate-flex">{group.name}</h3>
                    <p className="text-caption text-faint">{permCount} · {adminCount}</p>
                </div>
                <div className="flex gap-1 shrink-0">
                    <Button size="icon" variant="ghost" onClick={onEdit} title="Gruppe bearbeiten">
                        <PencilIcon className="size-4" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={onDelete} title="Gruppe löschen">
                        <Trash2Icon className="size-4" />
                    </Button>
                </div>
            </div>
            {group.admins.length ? (
                <div className="flex flex-wrap gap-1.5">
                    {group.admins.map((name) => (
                        <span key={name} className="plate px-2 py-0.5 text-label">{name}</span>
                    ))}
                </div>
            ) : null}
        </div>
    );
}


export default function AdminManagerPage() {
    const { hasPerm } = useAdminPerms();
    const openConfirmDialog = useOpenConfirmDialog();
    const [data, setData] = useState<AdminManagerListResp | undefined>();
    const [loadError, setLoadError] = useState<string | undefined>();
    const [groupDialog, setGroupDialog] = useState<GroupDialogState>(undefined);
    const [adminDialog, setAdminDialog] = useState<AdminDialogState>(undefined);
    const [groupsOfAdmin, setGroupsOfAdmin] = useState<AdminListItem | undefined>();

    const listApi = useBackendApi<AdminManagerListResp>({
        method: 'POST',
        path: '/adminManager/listAdmins',
        abortOnUnmount: true,
    });
    const deleteApi = useBackendApi<GenericApiOkResp, AdminGroupDeleteReq>({
        method: 'POST',
        path: '/adminManager/deleteGroup',
    });
    const deleteAdminApi = useBackendApi<AdminActionResp, AdminDeleteReq>({
        method: 'POST',
        path: '/adminManager/delete',
    });

    const refresh = useCallback(async () => {
        const resp = await listApi({ error: (msg) => setLoadError(msg) });
        if (!resp) return;
        setLoadError(undefined);
        setData(resp);
    }, []);

    useEffect(() => { refresh(); }, []);

    const handleDeleteGroup = (group: AdminGroup) => {
        openConfirmDialog({
            title: `Gruppe ${group.name} löschen`,
            message: group.admins.length
                ? `${group.admins.length} Admin(s) sind in dieser Gruppe und verlieren die Rechte, die sie gewährt. Eigene Rechte bleiben erhalten.`
                : 'Diese Gruppe ist leer und kann gefahrlos entfernt werden.',
            onConfirm: () => {
                deleteApi({
                    data: { id: group.id },
                    toastLoadingMessage: 'Gruppe wird gelöscht...',
                    success: (resp, toastId) => {
                        if ('error' in resp) {
                            txToast.error(resp.error, { id: toastId });
                            return;
                        }
                        txToast.success('Gruppe gelöscht.', { id: toastId });
                        refresh();
                    },
                });
            },
        });
    };

    //The backend refuses to let anyone edit or delete their own record
    const isSelf = (admin: AdminListItem) => {
        return admin.name.toLowerCase() === data?.currentAdminName.toLowerCase();
    };

    const handleDeleteAdmin = (admin: AdminListItem) => {
        openConfirmDialog({
            title: `Admin ${admin.name} entfernen`,
            message: 'Der Zugang zum Panel ist damit sofort weg. Der Spieler selbst bleibt unberührt.',
            confirmBtnVariant: 'destructive',
            onConfirm: () => {
                deleteAdminApi({
                    data: { name: admin.name },
                    toastLoadingMessage: 'Admin wird entfernt...',
                    success: (resp, toastId) => {
                        if (resp.type === 'danger') {
                            txToast.error(resp.message, { id: toastId });
                            return;
                        }
                        txToast.success('Admin entfernt.', { id: toastId });
                        refresh();
                    },
                });
            },
        });
    };

    if (!hasPerm('manage.admins')) {
        return <UnauthorizedPage />;
    }

    return (
        <div className="space-y-4 w-full">
            <PageHeader icon={<ShieldIcon />} title="Admins" />

            {loadError ? (
                <div className="surface p-4 text-danger-soft">{loadError}</div>
            ) : !data ? (
                <div className="flex justify-center p-8 text-faint">
                    <Loader2Icon className="animate-spin size-6" />
                </div>
            ) : (
                <Tabs defaultValue="admins" className="w-full">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                        <TabsList>
                            <TabsTrigger value="admins">Admins ({data.admins.length})</TabsTrigger>
                            <TabsTrigger value="groups">Gruppen ({data.groups.length})</TabsTrigger>
                        </TabsList>
                        <div className="flex gap-2">
                            <Button variant="solid" size="sm" onClick={() => setAdminDialog(null)}>
                                <PlusIcon className="size-4" /> Neuer Admin
                            </Button>
                            <Button variant="muted" size="sm" onClick={() => setGroupDialog(null)}>
                                <PlusIcon className="size-4" /> Neue Gruppe
                            </Button>
                        </div>
                    </div>

                    <TabsContent value="admins" className="pt-4">
                        <div className="surface divide-y divide-edge">
                            {data.admins.map((admin) => {
                                const groupNames = admin.groups
                                    .map((id) => data.groups.find((g) => g.id === id)?.name)
                                    .filter(Boolean) as string[];
                                return (
                                    <div key={admin.name} className="flex items-center gap-3 p-3">
                                        <div className="min-w-0 grow">
                                            <div className="flex items-center gap-2">
                                                <span className="font-semibold truncate-flex">{admin.name}</span>
                                                {admin.master ? (
                                                    <span className="plate px-1.5 py-0.5 text-micro uppercase tracking-[0.1em] text-amber">
                                                        Master
                                                    </span>
                                                ) : null}
                                            </div>
                                            <p className="text-caption text-faint">
                                                {admin.master
                                                    ? 'Master-Account – hat alle Berechtigungen'
                                                    : groupNames.length
                                                        ? `Gruppen: ${groupNames.join(', ')}`
                                                        : 'Keine Gruppen'}
                                            </p>
                                        </div>
                                        <Button
                                            size="sm"
                                            variant="muted"
                                            disabled={admin.master}
                                            onClick={() => setGroupsOfAdmin(admin)}
                                        >
                                            Gruppen
                                        </Button>
                                        <Button
                                            size="icon"
                                            variant="ghost"
                                            //the backend refuses both of these, so do not offer them
                                            disabled={isSelf(admin)}
                                            title={isSelf(admin) ? 'Dich selbst kannst du hier nicht bearbeiten' : 'Admin bearbeiten'}
                                            onClick={() => setAdminDialog(admin)}
                                        >
                                            <PencilIcon className="size-4" />
                                        </Button>
                                        <Button
                                            size="icon"
                                            variant="ghost"
                                            disabled={isSelf(admin) || admin.master}
                                            title={admin.master
                                                ? 'Der Master-Account kann nicht entfernt werden'
                                                : isSelf(admin) ? 'Dich selbst kannst du nicht entfernen' : 'Admin entfernen'}
                                            onClick={() => handleDeleteAdmin(admin)}
                                        >
                                            <Trash2Icon className="size-4" />
                                        </Button>
                                    </div>
                                );
                            })}
                        </div>
                    </TabsContent>

                    <TabsContent value="groups" className="pt-4">
                        {data.groups.length ? (
                            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                                {data.groups.map((group) => (
                                    <GroupCard
                                        key={group.id}
                                        group={group}
                                        onEdit={() => setGroupDialog(group)}
                                        onDelete={() => handleDeleteGroup(group)}
                                    />
                                ))}
                            </div>
                        ) : (
                            <div className="surface p-8 text-center text-muted-foreground">
                                <p>Noch keine Gruppen.</p>
                                <p className="text-caption opacity-75">
                                    Eine Gruppe bündelt Berechtigungen, damit du sie mehreren Admins auf einmal geben kannst.
                                </p>
                            </div>
                        )}
                    </TabsContent>
                </Tabs>
            )}

            {data && groupDialog !== undefined ? (
                <GroupEditDialog
                    group={groupDialog ?? undefined}
                    registeredPermissions={data.permissions}
                    onClose={() => setGroupDialog(undefined)}
                    onSaved={() => { setGroupDialog(undefined); refresh(); }}
                />
            ) : null}

            {data && adminDialog !== undefined ? (
                <AdminEditDialog
                    admin={adminDialog ?? undefined}
                    registeredPermissions={data.permissions}
                    onClose={() => setAdminDialog(undefined)}
                    onSaved={() => { setAdminDialog(undefined); refresh(); }}
                />
            ) : null}

            {data && groupsOfAdmin ? (
                <AdminGroupsDialog
                    admin={groupsOfAdmin}
                    groups={data.groups}
                    onClose={() => setGroupsOfAdmin(undefined)}
                    onSaved={() => { setGroupsOfAdmin(undefined); refresh(); }}
                />
            ) : null}
        </div>
    );
}
